import React, { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AOS from "aos";
import {
  ShieldCheck,
  User,
  Database,
  Globe,
  Lock,
  Mail,
  Cookie,
  FileText,
  Scale,
  RefreshCcw,
  Server,
  CreditCard,
  Receipt,
  AlertTriangle,
  Ban,
  Gavel,
  Bell,
  Handshake,
  Clock,
  IndianRupee,
  Calendar,
  Phone,
  MessageCircle,
  Store,
  Truck,
  Utensils,
  DollarSign,
  BadgeCheck,
} from "lucide-react";

const TermsCondition = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-in-out",
    });
  }, []);

  const ListItem = ({ icon: Icon, children, className = "" }) => (
    <li className="flex items-start gap-3 text-gray-700 leading-relaxed">
      <Icon className="w-5 h-5 text-orange-500 mt-1 shrink-0" />
      <span className={`leading-relaxed ${className}`}>{children}</span>
    </li>
  );

  return (
    <>
      <Header />
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-100 py-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center mb-16" data-aos="fade-down">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Terms of Service
            </h1>
            <p className="text-gray-600 text-lg">
              Last updated: January 9, 2025
            </p>
          </div>

          {/* Glass Card */}
          <div className=" space-y-12">
            <div
              className="space-y-4 text-gray-700 space-y-4"
              data-aos="fade-left"
            >
              <p className="font-semibold text-3xl lg:text-4xl text-gray-900">
                Terms of Service – Service Providers (TiffinTap App)
              </p>
              <p>Effective Date: 01 Sep 2025</p>
              <p>Effective Date: 15 Sep 2025</p>
              <p>App Name: TiffinTap</p>
              <p>Domain: www.tiffintap.com</p>
            </div>

            {/* 1 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                1. Agreement to Terms
              </h2>

              <p className="leading-10 space-y-8">
                These Terms of Service ("Terms") constitute a legally binding
                agreement between you ("Service Provider", "you", or "your") and
                TiffinTap ("Company", "we", "us", or "our"). By registering an
                account and using the TiffinTap mobile application (the "App"),
                you acknowledge that you have read, understood, and agreed to
                these Terms.
              </p>
              <p>
                If you do not agree with these Terms, you must discontinue using
                the App immediately.
              </p>
            </div>

            {/* 2 */}
            <div data-aos="fade-up" className="space-y-5">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                2. Scope of Services
              </h2>
              <p>
                TiffinTap provides a digital platform that enables service
                providers to:
              </p>
              <ul className="space-y-3">
                <ListItem icon={Store}>
                  Register and maintain their tiffin service details,
                </ListItem>

                <ListItem icon={User}>
                  Manage customer information and orders,
                </ListItem>

                <ListItem icon={Truck}>
                  Record deliveries and payment transactions,
                </ListItem>

                <ListItem icon={MessageCircle}>
                  Communicate with customers through integrated channels (e.g.,
                  call, WhatsApp, email),
                </ListItem>

                <ListItem icon={Bell}>
                  Receive platform notifications (e.g., order summaries,
                  reminders).
                </ListItem>
              </ul>
              <p>TiffinTap does not:</p>
              <ul className="space-y-4">
                <ListItem icon={Utensils}>
                  Prepare, supply, or deliver food,
                </ListItem>

                <ListItem icon={CreditCard}>
                  Collect payments from your customers, or
                </ListItem>

                <ListItem icon={AlertTriangle}>
                  Guarantee customer compliance with payments, orders, or
                  refunds.
                </ListItem>
              </ul>
              <p>
                All transactions for food services are strictly between you and
                your customers.
              </p>
            </div>

            {/* 3 */}
            <div data-aos="fade-up">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                3. Account Registration
              </h2>
              <ul className="space-y-3">
                <ListItem icon={BadgeCheck}>
                  You must register with a valid email, phone number, and OTP
                  verification.
                </ListItem>

                <ListItem icon={Lock}>
                  You are responsible for maintaining the confidentiality of
                  your credentials.
                </ListItem>

                <ListItem icon={Ban}>
                  False, misleading, or incomplete registration details may
                  result in account suspension.
                </ListItem>
              </ul>
            </div>

            {/* 4 */}
            <div data-aos="fade-up" className="space-y-4">
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">
                4. Subscription Plans, Billing & Payments
              </h2>
              <h3 className="font-semibold text-lg mb-3">
                4.1 Payment Methods
              </h3>
              <ul className="space-y-3 mb-6">
                <ListItem icon={CreditCard}>
                  Subscription fees must be paid in advance using approved
                  methods such as credit card, debit card, PayPal, or Stripe.
                </ListItem>

                <ListItem icon={Receipt}>
                  By submitting your payment details, you authorize TiffinTap
                  (or its third-party payment processor) to automatically charge
                  applicable subscription fees.
                </ListItem>

                <ListItem icon={ShieldCheck}>
                  You are responsible for ensuring that your chosen payment
                  method remains valid and has sufficient funds.
                </ListItem>
              </ul>

              <h3 className="font-semibold text-lg mb-3">4.2 Currency</h3>
              <ul className="space-y-3 mb-6">
                <ListItem icon={DollarSign}>
                  Subscription fees are displayed in United States Dollars (USD)
                  by default.
                </ListItem>

                <ListItem icon={Globe}>
                  Where available, fees may be converted and billed in your
                  local currency (e.g., CAD, GBP, EUR, INR, AUD).
                </ListItem>

                <ListItem icon={RefreshCcw}>
                  Exchange rates and conversion fees are determined by your bank
                  or payment provider and are outside TiffinTap's control.
                </ListItem>
              </ul>

              <h3 className="font-semibold text-lg mb-3">
                4.3 Billing Cycle & Renewals
              </h3>
              <ul className="space-y-3">
                <ListItem icon={Calendar}>
                  Subscription fees are charged on a recurring monthly basis.
                </ListItem>

                <ListItem icon={Calendar}>
                  Renewal occurs automatically on the same calendar day each
                  month (e.g., if you subscribed on the 10th, your billing date
                  will be the 10th of each subsequent month).
                </ListItem>

                <ListItem icon={Clock}>
                  If your billing date does not exist in a given month (e.g.,
                  February), renewal will occur on the last day of that month
                </ListItem>

                <ListItem icon={RefreshCcw}>
                  You authorize TiffinTap to automatically renew your
                  subscription unless you cancel prior to your next billing
                  date.
                </ListItem>
              </ul>
              <h3 className="font-semibold text-lg mb-3">
                4.4 Rolling Monthly Subscriptions (30-Day Cycle)
              </h3>
              <ul className="space-y-3">
                <ListItem icon={Calendar}>
                  When you subscribe, your billing cycle begins on the date of
                  activation and continues for 30 consecutive days.
                </ListItem>

                <ListItem icon={Clock}>
                  Your next billing due date will be exactly 30 days after
                  activation (e.g., if you subscribe on March 13, your next
                  payment will be due on April 12).
                </ListItem>

                <ListItem icon={RefreshCcw}>
                  Renewals will continue every 30 days thereafter, unless
                  cancelled.
                </ListItem>

                <ListItem icon={BadgeCheck}>
                  The app will display your active plan status and upcoming due
                  date on your dashboard.
                </ListItem>

                <ListItem icon={RefreshCcw}>
                  If you upgrade or downgrade your plan mid-cycle, the change
                  will apply immediately, and your billing cycle will reset to
                  the new activation date.
                </ListItem>
              </ul>
              <h3 className="font-semibold text-lg mb-3">
                4.5 Failed Payments
              </h3>
              <ul className="space-y-3">
                <ListItem icon={AlertTriangle}>
                  If your payment method is declined, you will receive a
                  notification and a grace period of [7 days] to update your
                  billing details.
                </ListItem>

                <ListItem icon={Ban}>
                  Failure to update within the grace period may result in
                  account suspension until payment is made.
                </ListItem>
              </ul>
              <h3 className="font-semibold text-lg mb-3">
                4.6 Non-Refundable Fees
              </h3>
              <ul className="space-y-3">
                <ListItem icon={Receipt}>
                  All subscription fees are prepaid and non-refundable, except
                  as required by applicable law.
                </ListItem>

                <ListItem icon={Ban}>
                  Cancelling your subscription will stop further charges, but
                  you will not receive a refund for the current billing period.
                </ListItem>
              </ul>
            </div>

            {/* 5 */}
            <div data-aos="fade-up" className="space-y-4">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                5. Customer Orders & Non-Payment Risks
              </h2>
              <ul className="space-y-3">
                <ListItem icon={DollarSign}>
                  You are solely responsible for setting your prices and
                  recording customer orders.
                </ListItem>

                <ListItem icon={Database}>
                  TiffinTap provides order logs and payment tracking tools only.
                </ListItem>

                <ListItem icon={AlertTriangle}>
                  If a customer fails to pay, it is your responsibility to
                  resolve the matter directly with the customer.
                </ListItem>

                <ListItem icon={ShieldCheck}>
                  TiffinTap has no liability for customer non-payment, disputes,
                  or defaults.
                </ListItem>
              </ul>
            </div>

            {/* 6 */}
            <div data-aos="fade-up" className="space-y-4">
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">
                6. Refunds & Cancellations (Service Provider to TiffinTap)
              </h2>
              <ul className="space-y-3">
                <ListItem icon={Calendar}>
                  Subscription cancellations take effect at the end of your
                  current billing cycle.
                </ListItem>

                <ListItem icon={Ban}>
                  No partial or prorated refunds are provided for unused time.
                </ListItem>

                <ListItem icon={Clock}>
                  Free trial accounts automatically convert to paid plans if not
                  cancelled before the trial period ends.
                </ListItem>
              </ul>
            </div>

            {/* 7-11 */}
            <div className="space-y-8" data-aos="fade-up">
              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  7. Notifications & Communications
                </h2>
                <ul className="space-y-3">
                  <ListItem icon={Bell}>
                    TiffinTap may send you order summaries, daily updates, or
                    important service notifications via email, WhatsApp, or
                    in-app alerts.
                  </ListItem>

                  <ListItem icon={Mail}>
                    You consent to receive such communications as part of using
                    the App.
                  </ListItem>

                  <ListItem icon={Clock}>
                    You remain responsible for reviewing your dashboard daily to
                    avoid missed orders.
                  </ListItem>
                </ul>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  8. Disputes with Customers
                </h2>
                <ul className="space-y-3">
                  <ListItem icon={Handshake}>
                    All disputes regarding food quality, delivery, pricing, or
                    refunds must be resolved directly between you and your
                    customers.
                  </ListItem>

                  <ListItem icon={Scale}>
                    TiffinTap is not a party to such disputes and bears no
                    liability.
                  </ListItem>

                  <ListItem icon={Database}>
                    You may use order and payment history logs from the App to
                    assist in resolving disputes.
                  </ListItem>
                </ul>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">9. Use of Data</h2>

                <ul className="space-y-3">
                  <ListItem icon={Database}>
                    You grant TiffinTap the right to process and store data you
                    provide (e.g., menus, prices, customer details) for platform
                    functionality.
                  </ListItem>

                  <ListItem icon={ShieldCheck}>
                    Customer data entered by you is considered your
                    responsibility. You must comply with applicable data privacy
                    laws when handling customer information.
                  </ListItem>
                </ul>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  10. Prohibited Conduct
                </h2>
                <p className="mt-1 mb-2">You may not use the App to:</p>
                <ul className="space-y-3">
                  <ListItem icon={Ban}>Misrepresent services,</ListItem>

                  <ListItem icon={AlertTriangle}>
                    Engage in fraudulent activity,
                  </ListItem>

                  <ListItem icon={MessageCircle}>
                    Spam customers with irrelevant communication,
                  </ListItem>

                  <ListItem icon={CreditCard}>
                    Circumvent payment obligations to TiffinTap.
                  </ListItem>
                </ul>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  11. Limitation of Liability
                </h2>
                <ul className="space-y-3">
                  <ListItem icon={ShieldCheck}>
                    TiffinTap is not responsible for business losses, lost
                    profits, or reputational harm arising from your use of the
                    App.
                  </ListItem>

                  <ListItem icon={Scale}>
                    Our maximum liability to you is limited to the subscription
                    fees paid by you in the three (3) months preceding the
                    claim.
                  </ListItem>
                </ul>
              </div>
            </div>
            <div className="space-y-8" data-aos="fade-up">
              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  12. Indemnification
                </h2>
                <p className="mt-2 mb-2">
                  You agree to indemnify, defend, and hold harmless TiffinTap
                  from any claims, damages, losses, or expenses (including legal
                  fees) arising from:
                </p>
                <ul className="space-y-3">
                  <ListItem icon={Utensils}>
                    Your provision of food services,
                  </ListItem>

                  <ListItem icon={Handshake}>Disputes with customers,</ListItem>

                  <ListItem icon={Ban}>Your breach of these Terms.</ListItem>
                </ul>
              </div>
              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  13. Governing Law & Jurisdiction
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed by and construed in accordance
                  with the laws of Gujarat, India. Disputes shall be subject to
                  the exclusive jurisdiction of the courts of that jurisdiction.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-semibold mb-2">14. Termination</h2>

                <ul className="space-y-3">
                  <ListItem icon={Ban}>
                    TiffinTap may suspend or terminate your account for breach
                    of these Terms, non-payment, or misuse of the App.
                  </ListItem>

                  <ListItem icon={RefreshCcw}>
                    You may terminate your account by cancelling your
                    subscription and discontinuing use.
                  </ListItem>
                </ul>
              </div>
              <div>
                <h2 className="text-3xl font-semibold mb-2">
                  15. Changes to Terms
                </h2>
                <p>
                  TiffinTap may update these Terms at any time. Continued use of
                  the App constitutes acceptance of revised Terms.
                </p>
              </div>
              <div>
                <h2 className="text-3xl font-semibold mb-4">16. Contact Us</h2>
                <p>
                  For support or questions regarding these Terms, please
                  contact:
                </p>
                <ul className="space-y-2">
                  <ListItem icon={Mail}>
                    <a
                      href="mailto:hello@webapprise.com"
                      className="text-orange-500 hover:underline transition-all ease-in-out duration-500"
                    >
                      hello@webapprise.com
                    </a>
                  </ListItem>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default TermsCondition;
